import * as React from 'react';
import { Input as InputPrimitive } from '@base-ui/react/input';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const inputVariants = cva(
  'h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-base transition-colors outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 md:text-sm dark:bg-input/30 dark:disabled:bg-input/80 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40',
  {
    variants: {
      appearance: {
        "filter-value": "data-invalid:ring-destructive/40 data-invalid:ring-1 data-has-issue:pe-7",
        default: '',
        editable:
          'rounded border-transparent bg-transparent px-1 text-sm shadow-none ring-0 hover:bg-background focus-visible:border-ring focus-visible:bg-background',
      },
      size: {
        default: '',
        filter: 'h-control-md rounded-full',
        compact: 'h-7 px-1.5',
      },
      inset: {
        default: '',
        icon: 'pl-8',
      },
      text: {
        default: '',
        decimal: 'font-mono text-xs font-medium tabular-nums',
        date: 'font-mono text-xs',
        muted: 'text-muted-foreground focus-visible:text-foreground',
        'mono-xs': 'font-mono text-xs',
        'mono-xs-numeric': 'font-mono text-xs tabular-nums',
        mono: 'font-mono',
      },
      tone: {
        default: '',
        warning: 'bg-warning/15 font-semibold text-warning-foreground',
      },
    },
    defaultVariants: {
      appearance: 'default',
      size: 'default',
      inset: 'default',
      text: 'default',
      tone: 'default',
    },
  },
);

function Input({
  className,
  type,
  appearance = 'default',
  size = 'default',
  inset = 'default',
  text = 'default',
  tone = 'default',
  ...props
}: Omit<React.ComponentProps<'input'>, 'size'> & VariantProps<typeof inputVariants>) {
  return (
    <InputPrimitive
      type={type}
      data-slot="input"
      className={cn(inputVariants({ appearance, size, inset, text, tone }), className)}
      {...props}
    />
  );
}

export { Input,  };
